/**
 * Dienst-Registry — die EINE Stelle, an der das Portal weiß, wohin es spricht.
 *
 * Anlass ist das SaaS-Design (Post #4701): Eine Kundeninstanz ist isoliert,
 * aber das Portal telefonierte an sieben Stellen fest verdrahtet zu Alex'
 * Infrastruktur. Isolation IST das Produkt; ein Portal, das nach Hause
 * telefoniert, macht sie unsichtbar kaputt.
 *
 * QUELLE DER WAHRHEIT ist die Instanz selbst (Vertrag mit Cloud, `77eb0c9`):
 * `GET /api/version → services` — DREI Zustände je Dienst, nicht zwei:
 *
 *   Wert ("https://…")   → genau diesen Ursprung nutzen
 *   null                 → Dienst existiert auf dieser Instanz NICHT
 *                          → Feature leise aus (kein Knopf, keine Fehlerflut)
 *   Schlüssel FEHLT      → FEDERATION_DEFAULTS[dienst] — der Bestand
 *
 * Die Pro-Dienst-Absenz (statt map-weit) ist Clouds Verfeinerung: Das Backend
 * kennt nur `auth`/`storage` aus env; für FE-direkte Dienste lässt es den
 * Schlüssel weg, und der Betreiber-Default greift — die Föderation bleibt
 * unberührt, ohne dass jemand etwas konfiguriert. Auf standalone erzwingt die
 * Instanz `null` statt leerer env — „aktiv leeren, nicht nur nicht setzen".
 *
 * WICHTIG FÜR AUFRUFER: `serviceOrigin` IMMER zur AUFRUFZEIT auslesen, nie in
 * eine Modul-Konstante einfrieren — die Instanz-Map kommt asynchron nach dem
 * Import, eine Konstante wäre auf einer Kundeninstanz für immer der
 * Betreiber-Default. Genau dieser Fehler stand hier bis `77eb0c9`-FE.
 *
 * cloud-api fehlt hier MIT ABSICHT: Es ist same-origin (`BASE = ''`) und zieht
 * damit von selbst auf jede Instanz mit.
 */

export interface ServiceOrigins {
  /** Anmeldung, Token-Erneuerung, Einstellungs-Sync. Ohne ihn kein Portal. */
  auth: string;
  /** Goals, Issues, Zähl-Abzeichen — FE-direkt, bewusst ohne Proxy (#1257). */
  issue: string;
  /** Anhänge, Bilder, Diktier-Uploads, Avatar-Clips. */
  storage: string;
  /** Sprachschicht: Mint, Realtime-Werkzeuge. */
  ai: string;
  /** Collab-Dokumente und Posts — die **API**. */
  content: string;
  /** Beitrags-**Oberfläche** (Menschenblick): Ziel für anklickbare Verweise und
   *  die eingebettete Beitragsansicht. Bewusst ein EIGENER Schlüssel, weil
   *  `content` die API meint und beides auf einer Kundeninstanz auseinander
   *  liegt: Davids Registry meldet `content: https://david.arkturian.com/svc/content`
   *  — das ist der API-Durchgriff, dahinter antwortet JSON. Ein Verweis dorthin
   *  landet auf keiner lesbaren Seite (Cloud, 18.08.: `/svc/content/post/33`).
   *  `null` heißt „keine Beitrags-Oberfläche hier" → Verweis bleibt Text, keine
   *  Einbettung. */
  content_web: string;
  /** Protokoll-Anzeige. */
  log: string;
  /** Konten-Auslastung (Anthropic/OpenAI-Profile) — BETREIBER-Daten, kein
   *  Instanz-Dienst. Auf einer Kundeninstanz existiert er nicht. */
  usage: string;
}

export type ServiceName = keyof ServiceOrigins;

/** Alex' Föderation — greift je Dienst nur, wenn die Instanz den Schlüssel
 *  weglässt (Bestand / FE-direkte Dienste im Föderationsmodus). */
// ZWEITES NETZ (Clouds Vorschlag 17.08.): Ein Tenant-Build kann die Vorgaben
// zur BAUZEIT auf SEINE Ursprünge setzen (VITE_<DIENST>_ORIGIN) — dann trifft
// selbst ein Aufruf, der der Boot-Barriere entgeht, nie ein Betreiber-Backend.
// Erstes Netz bleibt instanceReady(); dies hier ist Defense in Depth.
// Leerer String = „Dienst existiert nicht" (wie null in der Instanz-Map).
const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
const fromEnv = (key: string, fallback: string): string => {
  const v = env[key];
  return v === undefined ? fallback : v;
};
const FEDERATION_DEFAULTS: ServiceOrigins = {
  auth: fromEnv('VITE_AUTH_ORIGIN', 'https://auth-api.arkturian.com'),
  issue: fromEnv('VITE_ISSUE_ORIGIN', 'https://issue-api.arkturian.com'),
  storage: fromEnv('VITE_STORAGE_ORIGIN', 'https://api-storage.arkturian.com'),
  ai: fromEnv('VITE_AI_ORIGIN', 'https://api-ai.arkturian.com'),
  content: fromEnv('VITE_CONTENT_ORIGIN', 'https://content-api.arkturian.com'),
  content_web: fromEnv('VITE_CONTENT_WEB_ORIGIN', 'https://content.arkturian.com'),
  log: fromEnv('VITE_LOG_ORIGIN', 'https://log-api.arkturian.com'),
  usage: fromEnv('VITE_USAGE_ORIGIN', 'https://cloud-api.arkserver.arkturian.com'),
};

/** Instanz-Map aus /api/version.services. `undefined` = noch nicht geladen
 *  (oder ältere cloud-api ohne Feld) — dann gelten die Defaults vollständig. */
let instanceServices: Partial<Record<ServiceName, string | null>> | undefined;

const listeners = new Set<() => void>();

/**
 * Liest einen Dienst-Ursprung — zur Aufrufzeit, nie als Konstante kopieren.
 * `null` heißt: Dienst existiert auf dieser Instanz nicht; der Aufrufer lässt
 * sein Feature leise weg statt ins Leere zu telefonieren.
 */
export function serviceOrigin(name: ServiceName): string | null {
  if (instanceServices && name in instanceServices) {
    const v = instanceServices[name];
    return v === null ? null : (v || FEDERATION_DEFAULTS[name]);
  }
  // AUSNAHME vom Bestands-Rückfall, und zwar eine, die Schaden verhindert:
  // Deklariert eine Instanz ihren EIGENEN Inhalte-Dienst, aber (noch) keine
  // Oberfläche dazu, dann ist die Betreiber-Oberfläche die FALSCHE Antwort —
  // `content.arkturian.com/post/33` zeigt einem Kunden Beitrag 33 DES
  // BETREIBERS, nicht seinen eigenen. Ein Verweis, der still den falschen
  // Inhalt zeigt, ist schlimmer als gar keiner; also `null` → der Verweis
  // bleibt Text, bis die Instanz `content_web` deklariert.
  if (name === 'content_web' && instanceServices && 'content' in instanceServices) {
    return null;
  }
  // Und dieselbe Logik für BETREIBER-Daten: `usage` ist die Konten-Auslastung
  // der Föderations-Profile — auf einer Kundeninstanz gibt es sie nicht. Meldet
  // eine Instanz überhaupt eine Dienst-Karte (also beschreibt sie sich selbst)
  // und lässt `usage` weg, heißt das „hier nicht", nicht „nimm die des
  // Betreibers". Sonst fragt Davids Portal die Konten-API von arkserver — mit
  // Davids Token, den arkserver nicht kennt: 401, Anzeige leer (Alex, 18.08.:
  // „Usage zeigt nichts an"). Vorher war es schlimmer als leer — vor dem
  // Registry-Umbau kamen dort ALEX' Konten an (Screenshot 119751).
  //
  // Die leere Karte `{}` der Föderationsknoten zählt NICHT als Selbstbeschreibung
  // — dort greift weiter die Vorgabe. Sobald cloud-api `edition` meldet
  // (tenant|operator), ersetze ich diese Heuristik durch die Auskunft selbst.
  if (name === 'usage' && instanceServices && Object.keys(instanceServices).length > 0) {
    // …aber NICHT „gar nicht": `/api/account-usage` ist eine cloud-api-Route,
    // und cloud-api ist same-origin — auf Davids Instanz ist sie vorhanden
    // (gemessen: 401, nicht 404). Dort gehört sie hin: Sie zeigt dann die
    // Konten DIESER Instanz, also Davids eigenes Abonnement, statt der des
    // Betreibers. Vorher zeigte sie fremde Konten (Screenshot 119751), gestern
    // nichts (401 gegen arkserver) — beides falsch, aus derselben Wurzel:
    // ein absoluter Betreiber-Ursprung für eine Auskunft über „hier".
    return globalThis.location?.origin || null;
  }
  return FEDERATION_DEFAULTS[name] || null;
}

/** Bequemer Wahrheitswert für UI-Gates (Mikrofon-Knopf, Goals-Badge …). */
export function serviceAvailable(name: ServiceName): boolean {
  return serviceOrigin(name) !== null;
}

/**
 * Übernimmt die Instanz-Map aus /api/version (useInstanceMode lädt sie mit dem
 * mode-Signal in EINEM Abruf). Fehlendes Feld / Fehler → nicht aufrufen, die
 * Defaults bleiben — der Bestand-Schluss ist dort dokumentiert.
 */
export function applyInstanceServices(map: Partial<Record<ServiceName, string | null>>): void {
  instanceServices = map;
  listeners.forEach((l) => l());
}

/** Reaktivität für React (useServiceOrigin) — kein React-Import hier. */
export function subscribeServiceOrigins(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
